import { useEffect, useRef, useState, type ReactNode } from "react";
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Accessible from "@mui/icons-material/Accessible";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import CallOutlined from "@mui/icons-material/CallOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import ChairOutlined from "@mui/icons-material/ChairOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import DirectionsOutlined from "@mui/icons-material/DirectionsOutlined";
import DragIndicator from "@mui/icons-material/DragIndicator";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import FacebookOutlined from "@mui/icons-material/FacebookOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import Instagram from "@mui/icons-material/Instagram";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import LocalParkingOutlined from "@mui/icons-material/LocalParkingOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import MeetingRoomOutlined from "@mui/icons-material/MeetingRoomOutlined";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import Star from "@mui/icons-material/Star";
import StarOutline from "@mui/icons-material/StarOutline";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import SwapVert from "@mui/icons-material/SwapVert";
import VolunteerActivismOutlined from "@mui/icons-material/VolunteerActivismOutlined";
import WifiOutlined from "@mui/icons-material/WifiOutlined";
import { FIELD_ICONS, toneColor, type Audience, type VisibilityField } from "../../venues/websiteVisibility";

export interface PageSection {
  key: string;
  label: string;
}

/** Sections that run full width on the website; the contact card sits beside everything before the first one. */
const WIDE = new Set(["streams", "reviews"]);

const SECTION_ICONS: Record<string, ReactNode> = {
  about: <NotesOutlined />,
  amenities: <ChecklistOutlined />,
  layouts: <ChairOutlined />,
  rooms: <MeetingRoomOutlined />,
  streams: <PlayCircleOutline />,
  reviews: <StarOutline />,
};

/** What the phone shows: a real venue (publishing panel) or the sample (settings page). */
export interface PreviewVenue {
  name: string;
  type: string;
  area: string;
  address: string;
  capacity: number | null;
  coverUrl: string | null;
  photoCount: number;
  description: string;
  amenities: string[];
  layouts: { name: string; capacity: number | null }[];
  rooms: { name: string; capacity: number | null }[];
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  rating: { average: number; count: number } | null;
  chemaUrl?: string | null;
}

export const SAMPLE_VENUE: PreviewVenue = {
  name: "Riverside Chapel",
  type: "Chapel",
  area: "Avondale, Harare",
  address: "12 River Rd",
  capacity: 250,
  coverUrl: null,
  photoCount: 8,
  description: "",
  amenities: ["Parking", "Wheelchair access", "Seating"],
  layouts: [{ name: "Theatre", capacity: 250 }, { name: "Banquet", capacity: 120 }],
  rooms: [{ name: "Side chapel", capacity: 40 }, { name: "Family room", capacity: 15 }],
  contactName: "Jane Moyo",
  contactPhone: "+263 77 123 4567",
  contactEmail: "events@riverside.co.zw",
  rating: { average: 4.8, count: 12 },
};

function amenityIcon(name: string) {
  const text = name.toLowerCase();
  if (text.includes("park")) return <LocalParkingOutlined />;
  if (text.includes("wi-fi") || text.includes("wifi") || text.includes("internet")) return <WifiOutlined />;
  if (text.includes("wheel") || text.includes("access")) return <Accessible />;
  if (text.includes("seat") || text.includes("chair")) return <ChairOutlined />;
  return <CheckCircle />;
}
const RANK: Record<Audience, number> = { hidden: 0, members: 1, everyone: 2 };

/** Where an extra field shows on the website; anything unknown is an amenity, as on the website. */
const showsIn = (field: VisibilityField) =>
  ["layouts", "visiting", "location", "streams"].includes(field.shows_in ?? "") ? (field.shows_in as string) : "amenities";

type Viewer = "visitor" | "member";

export type Mode = Viewer | "arrange";

const MODES: { value: Mode; label: string; icon: ReactNode }[] = [
  { value: "visitor", label: "Visitor", icon: <PublicOutlined /> },
  { value: "member", label: "Member", icon: <LockOutlined /> },
  { value: "arrange", label: "Arrange", icon: <SwapVert /> },
];

export type TemplateId = "modern" | "luxury" | "vibrant" | "studio";

export interface TemplateConfig {
  id: TemplateId;
  name: string;
  tagline: string;
  badge: string;
  accent: string;
  soft: string;
  fontFamily: string;
  headingFont: string;
  cardRadius: number | string;
  pillRadius: number | string;
  gradient: string;
  heroOverlay: string;
}

export const TEMPLATES: Record<TemplateId, TemplateConfig> = {
  modern: {
    id: "modern",
    name: "Modern Minimalist",
    tagline: "Default layout: clean lines, high-contrast clarity & contemporary format",
    badge: "Clean & Modern",
    accent: "#724E41",
    soft: "#F8FAFC",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFont: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    cardRadius: "10px",
    pillRadius: "999px",
    gradient: "linear-gradient(145deg, #0F172A 0%, #334155 50%, #724E41 100%)",
    heroOverlay: "rgba(15, 23, 42, 0.45)",
  },
  luxury: {
    id: "luxury",
    name: "Luxury Heritage",
    tagline: "Opulent serif headers, champagne gold accents & grand editorial hospitality format",
    badge: "Editorial & Opulent",
    accent: "#B45309",
    soft: "#FFFBEB",
    fontFamily: "Georgia, 'Times New Roman', serif",
    headingFont: "Georgia, 'Times New Roman', serif",
    cardRadius: "4px",
    pillRadius: "4px",
    gradient: "linear-gradient(145deg, #451A03 0%, #78350F 50%, #B45309 100%)",
    heroOverlay: "rgba(69, 26, 3, 0.45)",
  },
  vibrant: {
    id: "vibrant",
    name: "Vibrant Showcase",
    tagline: "High-energy event presentation with floating glassmorphic card & bento grid",
    badge: "Dynamic & Bold",
    accent: "#724E41",
    soft: "#FFF8F1",
    fontFamily: "system-ui, -apple-system, sans-serif",
    headingFont: "system-ui, -apple-system, sans-serif",
    cardRadius: "18px",
    pillRadius: "999px",
    gradient: "linear-gradient(145deg, #5C3423 0%, #724E41 60%, #CB8750 100%)",
    heroOverlay: "rgba(92, 52, 35, 0.45)",
  },
  studio: {
    id: "studio",
    name: "Split Studio",
    tagline: "Architectural split-screen layout with sticky media showcase & editorial specs",
    badge: "Architectural & Split",
    accent: "#0F172A",
    soft: "#F8FAFC",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    headingFont: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    cardRadius: "6px",
    pillRadius: "4px",
    gradient: "linear-gradient(145deg, #0F172A 0%, #334155 60%, #475569 100%)",
    heroOverlay: "rgba(15, 23, 42, 0.45)",
  },
};

/**
 * A phone-sized venue page drawn with sample content, showing exactly which
 * sections and details a visitor or a signed-in member gets, in the arranged
 * order. In Arrange mode the sections can be dragged (or moved with the arrows).
 */
export default function WebsitePreview({
  fields,
  audienceOf,
  sections,
  order,
  onOrderChange,
  unlocks,
  venue = SAMPLE_VENUE,
  modes = ["visitor", "member", "arrange"],
  template = "modern",
}: {
  fields: VisibilityField[];
  audienceOf: (field: VisibilityField) => Audience;
  sections: PageSection[];
  order: string[];
  /** Missing when the viewer can't edit, so Arrange is read-only. */
  onOrderChange?: (order: string[]) => void;
  /** What signing in unlocks, for the sign-in card. */
  unlocks: string[];
  /** The venue drawn in the phone; the sample one unless given. */
  venue?: PreviewVenue;
  /** Which choices the switch offers; the publishing panel leaves out Arrange. */
  modes?: Mode[];
  template?: TemplateId;
}) {
  const [mode, setMode] = useState<Mode>("visitor");
  const arranging = mode === "arrange";
  const viewer: Viewer = mode === "member" ? "member" : "visitor";
  const currentTemplate = TEMPLATES[template] ?? TEMPLATES.modern;
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [template]);
  const byKey = Object.fromEntries(fields.map((field) => [field.key, field]));
  const audience = (key: string): Audience => (byKey[key] ? audienceOf(byKey[key]) : "hidden");
  const sees = (key: string) => audience(key) === "everyone" || (viewer === "member" && audience(key) === "members");

  /** Added extra fields that join a given part of the page (backend: PublicField.shows_in). */
  const placed = (where: string) => fields.filter((field) => field.optional && showsIn(field) === where).map((field) => field.key);

  /** Who gets a whole section: the most open of the fields that make it up. */
  const sectionAudience = (key: string): Audience => {
    const keys =
      key === "about" ? ["description"]
      : key === "amenities" ? ["amenities", ...placed("amenities")]
      : key === "layouts" ? ["layouts", ...placed("layouts")]
      : [key];
    return keys.map(audience).reduce<Audience>((best, next) => (RANK[next] > RANK[best] ? next : best), "hidden");
  };
  const label = (key: string) => sections.find((section) => section.key === key)?.label ?? key;

  return (
    <Stack spacing={2} alignItems="center">
      {/* One switch: look at the page as a visitor or a member, or arrange it. Only one is ever on. */}
      <Box
        role="radiogroup"
        aria-label="Preview mode"
        sx={{ display: "inline-grid", gridTemplateColumns: `repeat(${modes.length}, 1fr)`, gap: 0.25, p: 0.375, borderRadius: 2.5, bgcolor: "#F1F3F7" }}
      >
        {MODES.filter((option) => modes.includes(option.value)).map((option) => {
          const selected = mode === option.value;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              onClick={() => setMode(option.value)}
              sx={{
                minWidth: 96,
                height: 34,
                px: 1.5,
                gap: 0.75,
                borderRadius: 2,
                fontSize: 13,
                fontWeight: selected ? 700 : 600,
                color: selected ? "text.primary" : "text.secondary",
                bgcolor: selected ? "background.paper" : "transparent",
                boxShadow: selected ? "0 1px 2px rgba(16,24,40,.08), 0 1px 3px rgba(16,24,40,.10)" : "none",
                transition: "background-color .15s, color .15s, box-shadow .15s",
                "& svg": { fontSize: 17, color: selected ? currentTemplate.accent : "inherit" },
                "&:hover": selected ? {} : { color: "text.primary" },
              }}
            >
              {option.icon}
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>

      <Phone scrollRef={scrollRef}>
        {arranging ? (
          <ArrangeList order={order} label={label} sectionAudience={sectionAudience} onOrderChange={onOrderChange} />
        ) : (
          <PagePreview
            order={order}
            viewer={viewer}
            sees={sees}
            fields={fields}
            sectionAudience={sectionAudience}
            label={label}
            unlocks={viewer === "visitor" ? unlocks : []}
            venue={venue}
            tpl={currentTemplate}
          />
        )}
      </Phone>
    </Stack>
  );
}

function Phone({ children, scrollRef }: { children: ReactNode; scrollRef?: React.Ref<HTMLDivElement> }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 330, p: 1.25, borderRadius: "42px", bgcolor: "#111827", boxShadow: "0 24px 60px rgba(17,24,39,.28), inset 0 0 0 2px #2b3445" }}>
      <Box sx={{ position: "relative", borderRadius: "33px", overflow: "hidden", bgcolor: "#fff", height: 640 }}>
        <Box sx={{ position: "absolute", zIndex: 2, top: 9, left: "50%", transform: "translateX(-50%)", width: 84, height: 22, borderRadius: 999, bgcolor: "#111827" }} />
        <Box ref={scrollRef} sx={{ height: "100%", overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, color: "#1f2937" }}>{children}</Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ preview */

const ACCENT = "#7a4a33";
const SOFT = "#f5f1ee";

function PagePreview({
  order,
  viewer,
  sees,
  fields,
  sectionAudience,
  label,
  unlocks,
  venue,
  tpl = TEMPLATES.modern,
}: {
  order: string[];
  viewer: Viewer;
  sees: (key: string) => boolean;
  fields: VisibilityField[];
  sectionAudience: (key: string) => Audience;
  label: (key: string) => string;
  unlocks: string[];
  venue: PreviewVenue;
  tpl?: TemplateConfig;
}) {
  const theme = useTheme();
  const membersTone = toneColor(theme, "members");
  const visible = (key: string) => sectionAudience(key) === "everyone" || (viewer === "member" && sectionAudience(key) === "members");
  // Like the website, a section with nothing to show isn't there at all.
  const extras = (where: string) => fields.some((field) => field.optional && showsIn(field) === where);
  const hasContent: Record<string, boolean> = {
    about: venue === SAMPLE_VENUE || Boolean(venue.description.trim()),
    amenities: venue.amenities.length > 0 || extras("amenities"),
    layouts: venue.layouts.length > 0 || extras("layouts"),
    rooms: venue.rooms.length > 0,
  };
  const shown = order.filter((key) => sectionAudience(key) !== "hidden" && hasContent[key] !== false);
  const firstWide = shown.findIndex((key) => WIDE.has(key));
  const beside = firstWide === -1 ? shown : shown.slice(0, firstWide);
  const below = firstWide === -1 ? [] : shown.slice(firstWide);

  const section = (key: string) =>
    visible(key) ? (
      <MiniSection key={key} title={label(key)} headingFont={tpl.headingFont}>
        <SectionBody sectionKey={key} sees={sees} fields={fields} venue={venue} tpl={tpl} />
      </MiniSection>
    ) : (
      // Visitors don't get this section; drawn faintly so it's clear what signing in adds.
      <Box key={key} sx={{ mx: 2, my: 1.25, px: 1.5, py: 1.1, borderRadius: tpl.cardRadius, border: "1px dashed", borderColor: alpha(membersTone, 0.45), display: "flex", alignItems: "center", gap: 1, color: membersTone }}>
        <LockOutlined sx={{ fontSize: 15 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{label(key)}</Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, opacity: 0.8 }}>Members</Typography>
      </Box>
    );

  const isLuxury = tpl.id === "luxury";
  const isVibrant = tpl.id === "vibrant";
  const isStudio = tpl.id === "studio";
  const isModern = !isLuxury && !isVibrant && !isStudio;

  const facts = [
    sees("capacity") && venue.capacity ? { icon: <GroupsOutlined />, k: "Capacity", v: `Up to ${venue.capacity}` } : null,
    { icon: <StorefrontOutlined />, k: "Type", v: venue.type },
    { icon: <LocationOnOutlined />, k: sees("street_address") && venue.address ? "Address" : "Area", v: sees("street_address") && venue.address ? venue.address : venue.area },
    sees("reviews") ? { icon: <Star />, k: "Rating", v: venue.rating ? `${venue.rating.average.toFixed(1)} · ${venue.rating.count}` : "No reviews yet" } : null,
  ].filter(Boolean) as { icon: ReactNode; k: string; v: string }[];

  return (
    <Box sx={{ pb: 3, fontFamily: tpl.fontFamily, bgcolor: isLuxury ? "#FCFAF6" : isStudio ? "#FAFAFA" : "#FFFFFF" }}>
      {/* =========================================================
          1. LUXURY HERITAGE LAYOUT
          ========================================================= */}
      {isLuxury && (
        <>
          {/* Centered Royal Masthead */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5, textAlign: "center" }}>
            <Typography sx={{ fontSize: 19, fontWeight: 600, fontFamily: tpl.headingFont, color: "#451A03", lineHeight: 1.25 }}>
              {venue.name}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ mt: 0.75, color: "#92400E", fontSize: 11, fontFamily: tpl.headingFont }}>
              <Stack direction="row" spacing={0.3} alignItems="center">
                <LocationOnOutlined sx={{ fontSize: 13, color: "#B45309" }} />
                <span>{venue.area}</span>
              </Stack>
              {sees("reviews") && venue.rating && (
                <Stack direction="row" spacing={0.3} alignItems="center">
                  <Star sx={{ fontSize: 13, color: "#D97706" }} />
                  <span>{venue.rating.average.toFixed(1)} ({venue.rating.count})</span>
                </Stack>
              )}
            </Stack>
          </Box>

          {/* Gilded Framed Picture Gallery */}
          <Box sx={{ px: 2 }}>
            <Box sx={{ p: "3px", borderRadius: "10px", background: "linear-gradient(180deg, #FDE68A, #D97706)", boxShadow: "0 6px 16px rgba(120,53,15,0.12)" }}>
              <Box sx={{ position: "relative", height: 160, borderRadius: "7px", overflow: "hidden", background: venue.coverUrl ? `center / cover no-repeat url("${venue.coverUrl.replace(/"/g, "%22")}")` : tpl.gradient }}>
                {venue.coverUrl && <Box sx={{ position: "absolute", inset: 0, bgcolor: tpl.heroOverlay }} />}
                {!venue.coverUrl && (
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1.5, textAlign: "center", color: "#FEF3C7" }}>
                    <StorefrontOutlined sx={{ fontSize: 28, opacity: 0.85, mb: 0.5 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{venue.name}</Typography>
                    <Typography sx={{ fontSize: 9.5, opacity: 0.75 }}>Cover photo showcase</Typography>
                  </Box>
                )}
                {sees("gallery") && venue.photoCount > 1 && (
                  <Box sx={{ position: "absolute", right: 8, bottom: 8, px: 0.9, py: 0.25, borderRadius: "4px", bgcolor: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 10, fontWeight: 700 }}>
                    1 / {venue.photoCount}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Gilded Standalone Stat Plaques */}
          <Box sx={{ px: 2, mt: 1.75, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {facts.map((item) => (
              <Box key={item.k} sx={{ p: 1, borderRadius: "6px", bgcolor: "#FFFDF9", border: "1px solid #FDE68A", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#FEF3C7", color: "#B45309", display: "grid", placeItems: "center", mb: 0.5, "& svg": { fontSize: 13 } }}>
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.8, color: "#92400E", textTransform: "uppercase" }}>{item.k}</Typography>
                <Typography noWrap sx={{ fontSize: 11, fontWeight: 700, fontFamily: tpl.headingFont, color: "#451A03" }}>{item.v}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* =========================================================
          2. VIBRANT SHOWCASE LAYOUT
          ========================================================= */}
      {isVibrant && (
        <>
          {/* Full-bleed Hero Gallery */}
          <Box sx={{ position: "relative", height: 180, mx: 1.5, mt: 1.5, borderRadius: "20px", overflow: "hidden", border: "2px solid #E8D8CB", background: venue.coverUrl ? `center / cover no-repeat url("${venue.coverUrl.replace(/"/g, "%22")}")` : tpl.gradient, boxShadow: "0 10px 24px rgba(114,78,65,0.15)" }}>
            {venue.coverUrl && <Box sx={{ position: "absolute", inset: 0, bgcolor: tpl.heroOverlay }} />}
            {!venue.coverUrl && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1.5, textAlign: "center", color: "#FDF2E9" }}>
                <StorefrontOutlined sx={{ fontSize: 28, opacity: 0.9, mb: 0.5 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 900, letterSpacing: "-0.01em" }}>{venue.name}</Typography>
                <Typography sx={{ fontSize: 9.5, opacity: 0.8 }}>Dynamic Media Showcase</Typography>
              </Box>
            )}
            <Box sx={{ position: "absolute", top: 10, left: 10, px: 1, py: 0.35, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.92)", color: "#724E41", fontSize: 9.5, fontWeight: 800, backdropFilter: "blur(4px)" }}>
              ⚡ {venue.type}
            </Box>
            {sees("gallery") && venue.photoCount > 1 && (
              <Box sx={{ position: "absolute", right: 10, bottom: 10, px: 1, py: 0.25, borderRadius: "999px", bgcolor: "rgba(17,24,39,0.7)", color: "#fff", fontSize: 10, fontWeight: 700 }}>
                1 / {venue.photoCount}
              </Box>
            )}
          </Box>

          {/* Overlapping Floating Card with Tag Ribbon */}
          <Box sx={{ mx: 2, mt: -3, position: "relative", zIndex: 2, p: 1.75, borderRadius: "18px", bgcolor: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", border: "2px solid #E8D8CB", boxShadow: "0 10px 25px rgba(114,78,65,0.1)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Box sx={{ px: 1, py: 0.25, borderRadius: "999px", bgcolor: "#FDF2E9", color: "#724E41", fontSize: 9.5, fontWeight: 800, textTransform: "uppercase" }}>
                {venue.type}
              </Box>
              {sees("reviews") && venue.rating && (
                <Stack direction="row" spacing={0.3} alignItems="center">
                  <Star sx={{ fontSize: 13, color: "#f59e0b" }} />
                  <b style={{ fontSize: 11, color: "#1f2937" }}>{venue.rating.average.toFixed(1)}</b>
                </Stack>
              )}
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.2, color: "#0F172A", letterSpacing: "-0.01em" }}>
              {venue.name}
            </Typography>
            {/* Live Tag Ribbon */}
            <Box sx={{ mt: 1.25, pt: 1, borderTop: "1px solid #E8D8CB", display: "flex", flexWrap: "wrap", gap: 0.6 }}>
              <Box sx={{ px: 1, py: 0.35, borderRadius: "999px", bgcolor: "#FFF8F1", border: "1px solid #E8D8CB", color: "#5C3423", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.4 }}>
                <LocationOnOutlined sx={{ fontSize: 12, color: "#724E41" }} />
                <span>{venue.area}</span>
              </Box>
              {sees("capacity") && venue.capacity && (
                <Box sx={{ px: 1, py: 0.35, borderRadius: "999px", bgcolor: "#FFF8F1", border: "1px solid #E8D8CB", color: "#5C3423", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.4 }}>
                  <GroupsOutlined sx={{ fontSize: 12, color: "#724E41" }} />
                  <span>Up to {venue.capacity}</span>
                </Box>
              )}
              {venue.amenities.length > 0 && (
                <Box sx={{ px: 1, py: 0.35, borderRadius: "999px", bgcolor: "#FDF2E9", border: "1px solid #E8D8CB", color: "#724E41", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.4 }}>
                  <CheckCircle sx={{ fontSize: 12, color: "#CB8750" }} />
                  <span>{venue.amenities.length} amenities</span>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* =========================================================
          3. SPLIT STUDIO / ARCHITECTURAL LAYOUT
          ========================================================= */}
      {isStudio && (
        <>
          {/* Architectural Top Bar */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 9, fontFamily: "monospace", letterSpacing: 1.2, color: "#64748B", textTransform: "uppercase" }}>
              [ DIRECTORY ]
            </Typography>
            <Typography sx={{ fontSize: 9, fontFamily: "monospace", letterSpacing: 1.2, color: "#94A3B8", textTransform: "uppercase" }}>
              STUDIO // SPEC SHEET
            </Typography>
          </Box>

          {/* Architectural Photo Showcase */}
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Box sx={{ position: "relative", height: 140, borderRadius: "6px", overflow: "hidden", border: "1px solid #CBD5E1", background: venue.coverUrl ? `center / cover no-repeat url("${venue.coverUrl.replace(/"/g, "%22")}")` : tpl.gradient }}>
              {venue.coverUrl && <Box sx={{ position: "absolute", inset: 0, bgcolor: tpl.heroOverlay }} />}
              {!venue.coverUrl && (
                <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1.5, textAlign: "center", color: "#CBD5E1" }}>
                  <StorefrontOutlined sx={{ fontSize: 26, opacity: 0.8, mb: 0.5 }} />
                  <Typography sx={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>{venue.name}</Typography>
                  <Typography sx={{ fontSize: 9, fontFamily: "monospace", opacity: 0.7 }}>FIG 01 // ARCHITECTURAL VIEW</Typography>
                </Box>
              )}
              <Box sx={{ position: "absolute", top: 8, left: 8, px: 0.75, py: 0.2, borderRadius: "3px", bgcolor: "#0F172A", color: "#fff", fontSize: 8.5, fontFamily: "monospace", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                {venue.type}
              </Box>
              {sees("gallery") && venue.photoCount > 1 && (
                <Box sx={{ position: "absolute", right: 8, bottom: 8, px: 0.75, py: 0.2, borderRadius: "3px", bgcolor: "rgba(15,23,42,0.85)", color: "#fff", fontSize: 9, fontFamily: "monospace" }}>
                  FIG 01 // {venue.photoCount}
                </Box>
              )}
            </Box>
          </Box>

          {/* Architectural Title & Spec Sheet Table */}
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2, color: "#0F172A", letterSpacing: "-0.02em" }}>
              {venue.name}
            </Typography>
            <Typography sx={{ fontSize: 10.5, fontFamily: "monospace", color: "#64748B", mt: 0.25 }}>
              {venue.area}{fields.some((field) => showsIn(field) === "location" && field.optional && sees(field.key)) ? " // ZW" : ""}
            </Typography>

            {/* Technical Spec Sheet Table */}
            <Box sx={{ mt: 1.5, borderRadius: "6px", border: "1px solid #CBD5E1", bgcolor: "#FFFFFF", overflow: "hidden" }}>
              {facts.map((item, index) => (
                <Box key={item.k} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.25, py: 0.65, borderTop: index ? "1px solid #F1F5F9" : 0, fontSize: 11 }}>
                  <Typography sx={{ fontSize: 9, fontFamily: "monospace", fontWeight: 700, letterSpacing: 0.8, color: "#64748B", textTransform: "uppercase" }}>
                    {item.k}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>
                    {item.v}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* =========================================================
          4. MODERN MINIMALIST (Classic Default Format)
          ========================================================= */}
      {isModern && (
        <>
          {/* Cover and gallery */}
          <Box sx={{ position: "relative", height: 178, background: venue.coverUrl ? `center / cover no-repeat url("${venue.coverUrl.replace(/"/g, "%22")}")` : tpl.gradient }}>
            {venue.coverUrl && (
              <Box sx={{ position: "absolute", inset: 0, bgcolor: tpl.heroOverlay }} />
            )}
            {!venue.coverUrl && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1.5, textAlign: "center", color: "#FFFFFF" }}>
                <StorefrontOutlined sx={{ fontSize: 28, opacity: 0.85, mb: 0.5 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{venue.name}</Typography>
                <Typography sx={{ fontSize: 9.5, opacity: 0.8 }}>Venue Cover Photo</Typography>
              </Box>
            )}
            <Box sx={{ position: "absolute", top: 10, left: 10, px: 1, py: 0.25, borderRadius: tpl.pillRadius, bgcolor: "rgba(17,24,39,.75)", color: "#fff", fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, backdropFilter: "blur(4px)" }}>
              {tpl.badge}
            </Box>
            {sees("gallery") && venue.photoCount > 1 && (
              <Box sx={{ position: "absolute", right: 10, bottom: 10, px: 1, py: 0.25, borderRadius: tpl.pillRadius, bgcolor: "rgba(17,24,39,.65)", color: "#fff", fontSize: 10.5, fontWeight: 700 }}>1 / {venue.photoCount}</Box>
            )}
          </Box>

          {/* Name and facts */}
          <Box sx={{ px: 2, pt: 1.75 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: tpl.accent, textTransform: "uppercase" }}>{venue.type}</Typography>
            <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2, fontFamily: tpl.headingFont }}>{venue.name}</Typography>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.5, color: "#6b7280", fontSize: 11.5 }}>
              <Stack direction="row" spacing={0.25} alignItems="center"><LocationOnOutlined sx={{ fontSize: 13 }} /><span>{venue.area}{fields.some((field) => showsIn(field) === "location" && field.optional && sees(field.key)) ? ", Zimbabwe" : ""}</span></Stack>
              {sees("reviews") && venue.rating && <Stack direction="row" spacing={0.25} alignItems="center"><Star sx={{ fontSize: 13, color: "#f59e0b" }} /><b style={{ color: "#1f2937" }}>{venue.rating.average.toFixed(1)}</b><span>({venue.rating.count})</span></Stack>}
            </Stack>
            <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", borderRadius: tpl.cardRadius, overflow: "hidden", bgcolor: "#e5e7eb", border: "1px solid #e5e7eb" }}>
              {facts.map((item) => (
                <Stack key={item.k} direction="row" spacing={0.75} alignItems="center" sx={{ bgcolor: tpl.soft, p: 1, "& svg": { fontSize: 15, color: tpl.accent } }}>
                  {item.icon}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af", textTransform: "uppercase" }}>{item.k}</Typography>
                    <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 700 }}>{item.v}</Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
          </Box>
        </>
      )}

      {beside.map(section)}
      <ContactCard sees={sees} fields={fields} venue={venue} tpl={tpl} />
      {venue.chemaUrl && (
        <Box sx={{ mx: 2, mt: 1.5, p: 1.5, borderRadius: tpl.cardRadius, bgcolor: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#059669", color: "#fff", display: "grid", placeItems: "center" }}>
              <VolunteerActivismOutlined sx={{ fontSize: 13 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>Chema Fundraiser</Typography>
              <Typography sx={{ fontSize: 9.5, color: "#059669", fontWeight: 700 }}>Interactive donation widget</Typography>
            </Box>
          </Stack>
          <Box sx={{ py: 0.75, px: 1, borderRadius: 1.5, bgcolor: "#f0fdf4", border: "1px dashed #a7f3d0", textAlign: "center" }}>
            <Typography sx={{ fontSize: 9.5, color: "#065f46", fontWeight: 700 }}>chema.co.zw fundraiser active</Typography>
          </Box>
        </Box>
      )}
      {unlocks.length > 0 && (
        <Box sx={{ mx: 2, mt: 1.5, p: 1.5, borderRadius: tpl.cardRadius, background: `linear-gradient(135deg, ${tpl.soft}, #fff)`, border: "1px solid #eadfd8" }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: alpha(tpl.accent, 0.14), display: "grid", placeItems: "center" }}>
              <LockOutlined sx={{ fontSize: 14, color: tpl.accent }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>See the full venue details</Typography>
          </Stack>
          <Stack spacing={0.4} sx={{ mt: 1 }}>
            {unlocks.slice(0, 5).map((item) => (
              <Stack key={item} direction="row" spacing={0.6} alignItems="center" sx={{ fontSize: 11, color: "#6b7280" }}>
                <CheckCircle sx={{ fontSize: 12, color: tpl.accent }} />
                <span>{item}</span>
              </Stack>
            ))}
            {unlocks.length > 5 && <Typography sx={{ fontSize: 11, color: "#9ca3af", pl: 2.25 }}>and {unlocks.length - 5} more</Typography>}
          </Stack>
          <Box sx={{ mt: 1.25, py: 0.75, borderRadius: tpl.pillRadius, bgcolor: tpl.accent, color: "#fff", textAlign: "center", fontSize: 12, fontWeight: 700 }}>Sign in</Box>
        </Box>
      )}
      {below.map(section)}
    </Box>
  );
}

function MiniSection({ title, children, headingFont }: { title: string; children: ReactNode; headingFont?: string }) {
  return (
    <Box sx={{ mx: 2, pt: 2, mt: 1.5, borderTop: "1px solid #eef0f3" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 800, fontFamily: headingFont ?? "Georgia, serif", mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

const Line = ({ width }: { width: string }) => <Box sx={{ height: 6, width, borderRadius: 3, bgcolor: "#e5e7eb", mb: 0.75 }} />;

function Tile({ icon, text, tpl }: { icon: ReactNode; text: string; tpl?: TemplateConfig }) {
  const accent = tpl?.accent ?? ACCENT;
  const soft = tpl?.soft ?? SOFT;
  const radius = tpl?.cardRadius ?? 1.5;
  return (
    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ px: 0.9, py: 0.75, borderRadius: radius, bgcolor: soft, fontSize: 10.5, fontWeight: 600, "& svg": { fontSize: 14, color: accent } }}>
      {icon}
      <span>{text}</span>
    </Stack>
  );
}

function SectionBody({ sectionKey, sees, fields, venue, tpl }: { sectionKey: string; sees: (key: string) => boolean; fields: VisibilityField[]; venue: PreviewVenue; tpl?: TemplateConfig }) {
  const accent = tpl?.accent ?? ACCENT;
  const largest = Math.max(1, ...venue.layouts.map((layout) => layout.capacity ?? 0), venue.capacity ?? 0);
  const extrasIn = (where: string) => fields.filter((field) => field.optional && showsIn(field) === where && sees(field.key));
  switch (sectionKey) {
    case "about":
      return venue.description.trim() ? (
        <Typography sx={{ fontSize: 11.5, color: "#4b5563", lineHeight: 1.5, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden", whiteSpace: "pre-line" }}>
          {venue.description}
        </Typography>
      ) : (
        <Box><Line width="100%" /><Line width="94%" /><Line width="70%" /></Box>
      );
    case "amenities":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {sees("amenities") && venue.amenities.slice(0, 6).map((amenity) => (
            <Tile key={amenity} icon={amenityIcon(amenity)} text={amenity} tpl={tpl} />
          ))}
          {/* Added extra fields with no other home read as amenities, e.g. "Power supply". */}
          {extrasIn("amenities").map((field) => (
            <Tile key={field.key} icon={FIELD_ICONS[field.key]} text={field.label} tpl={tpl} />
          ))}
        </Box>
      );
    case "layouts":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {sees("layouts") && venue.layouts.slice(0, 4).map((layout) => (
            <Box key={layout.name} sx={{ p: 1, borderRadius: tpl?.cardRadius ?? 1.5, border: "1px solid #eef0f3" }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "capitalize" }} noWrap>{layout.name}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{layout.capacity ?? "—"}<span style={{ fontSize: 9.5, color: "#9ca3af", fontWeight: 500 }}> guests</span></Typography>
              <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: "#f1f3f5" }}><Box sx={{ height: 4, width: `${Math.min(100, ((layout.capacity ?? 0) / largest) * 100)}%`, borderRadius: 2, bgcolor: alpha(accent, 0.7) }} /></Box>
            </Box>
          ))}
          {/* Floor plans: a button under the layouts that opens them. */}
          {extrasIn("layouts").map((field) => (
            <Stack key={field.key} direction="row" spacing={0.6} alignItems="center" sx={{ gridColumn: "1 / -1", justifySelf: "start", px: 1.25, py: 0.6, borderRadius: tpl?.cardRadius ?? 1.5, border: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, "& svg": { fontSize: 14, color: accent } }}>
              <MapOutlined />
              <span>View {field.label.replace(/s$/i, "").toLowerCase()}</span>
            </Stack>
          ))}
        </Box>
      );
    case "rooms":
      return (
        <Stack spacing={0.75}>
          {venue.rooms.slice(0, 4).map((room) => (
            <Stack key={room.name} direction="row" spacing={1} alignItems="center" sx={{ p: 0.9, borderRadius: tpl?.cardRadius ?? 1.5, border: "1px solid #eef0f3", "& svg": { fontSize: 15, color: accent } }}>
              <MeetingRoomOutlined />
              <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1 }} noWrap>{room.name}</Typography>
              {room.capacity && <Typography sx={{ fontSize: 10.5, color: "#6b7280" }}>{room.capacity} guests</Typography>}
            </Stack>
          ))}
        </Stack>
      );
    case "streams":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75 }}>
          {extrasIn("streams").map((field) => (
            <Typography key={field.key} sx={{ gridColumn: "1 / -1", fontSize: 10.5, color: "#6b7280", mt: -0.5 }}>
              {field.key === "number_of_cameras" ? "Filmed with up to 3 camera angles." : field.label}
            </Typography>
          ))}
          {[0, 1].map((index) => (
            <Box key={index} sx={{ borderRadius: tpl?.cardRadius ?? 1.5, overflow: "hidden", border: "1px solid #eef0f3" }}>
              <Box sx={{ height: 52, background: index ? "linear-gradient(135deg,#d6c3b6,#efe5dd)" : "linear-gradient(135deg,#b99a86,#e8d9cd)", display: "grid", placeItems: "center" }}>
                <PlayArrowRounded sx={{ fontSize: 20, color: "#fff" }} />
              </Box>
              <Box sx={{ p: 0.75 }}><Line width="85%" /><Line width="50%" /></Box>
            </Box>
          ))}
        </Box>
      );
    case "reviews":
      return (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{venue.rating ? venue.rating.average.toFixed(1) : "—"}</Typography>
            <Stack direction="row">{[0, 1, 2, 3, 4].map((index) => <Star key={index} sx={{ fontSize: 14, color: venue.rating && index < Math.round(venue.rating.average) ? "#f59e0b" : "#e5e7eb" }} />)}</Stack>
            <Typography sx={{ fontSize: 10.5, color: "#6b7280" }}>{venue.rating ? `${venue.rating.count} ${venue.rating.count === 1 ? "review" : "reviews"}` : "No reviews yet"}</Typography>
          </Stack>
          <Box sx={{ mt: 1, p: 1, borderRadius: tpl?.cardRadius ?? 1.5, border: "1px solid #eef0f3" }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, mb: 0.5 }}>Tendai M.</Typography>
            <Line width="95%" /><Line width="60%" />
          </Box>
        </Box>
      );
    default:
      return null;
  }
}

/** The venue's contact card, with only the parts this viewer gets. */
function ContactCard({ sees, fields, venue, tpl }: { sees: (key: string) => boolean; fields: VisibilityField[]; venue: PreviewVenue; tpl?: TemplateConfig }) {
  const accent = tpl?.accent ?? ACCENT;
  const soft = tpl?.soft ?? SOFT;
  const radius = tpl?.cardRadius ?? 2.5;
  const notes = fields.filter((field) => field.optional && showsIn(field) === "visiting" && sees(field.key));
  const person = sees("contact_name") || sees("contact_photo");
  const lines = [
    sees("contact_phone") && { key: "phone", icon: <CallOutlined />, text: venue.contactPhone || "Not provided" },
    sees("contact_email") && { key: "email", icon: <EmailOutlined />, text: venue.contactEmail || "Not provided" },
  ].filter(Boolean) as { key: string; icon: ReactNode; text: string }[];
  const socials = [["website", <LanguageOutlined key="w" />], ["facebook", <FacebookOutlined key="f" />], ["instagram", <Instagram key="i" />]].filter(([key]) => sees(key as string));
  const directions = sees("map");
  const address = sees("street_address");
  if (!person && !lines.length && !socials.length && !directions && !address && !notes.length) return null;

  const isLuxury = tpl?.id === "luxury";
  const isVibrant = tpl?.id === "vibrant";
  const isStudio = tpl?.id === "studio";

  return (
    <Box
      sx={{
        mx: 2,
        mt: 2,
        p: 1.5,
        borderRadius: radius,
        border: isLuxury ? "1px solid #E8DFD3" : isVibrant ? "2px solid #FECDD3" : isStudio ? "1px solid #CBD5E1" : "1px solid #eef0f3",
        bgcolor: isLuxury ? "#FFFDF9" : "#FFFFFF",
        boxShadow: isLuxury ? "0 4px 12px rgba(180,83,9,.06)" : isVibrant ? "0 4px 14px rgba(225,29,72,.1)" : isStudio ? "0 1px 4px rgba(15,23,42,.06)" : "0 2px 8px rgba(17,24,39,.05)",
      }}
    >
      {/* Like the website: phone or email alone still get the header, named after the venue. */}
      {person || lines.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              flexShrink: 0,
              border: isLuxury ? "2px solid #D97706" : isVibrant ? "2px solid #E11D48" : isStudio ? "2px solid #0F172A" : "none",
              background: sees("contact_photo")
                ? "linear-gradient(135deg,#c9a58c,#8a5a43)"
                : isLuxury
                ? "linear-gradient(135deg,#B45309,#78350F)"
                : isVibrant
                ? "linear-gradient(135deg,#E11D48,#BE123C)"
                : isStudio
                ? "linear-gradient(135deg,#0F172A,#334155)"
                : "linear-gradient(160deg,#8a5a43,#e9d3c0)",
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: isLuxury ? "#B45309" : isVibrant ? "#E11D48" : isStudio ? "#0F172A" : "#9ca3af", fontFamily: isStudio ? "monospace" : "inherit" }}>
              {isLuxury ? "VENUE CONCIERGE" : isVibrant ? "VENUE HOST" : isStudio ? "INQUIRIES // CONCIERGE" : "YOUR VENUE CONTACT"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, fontFamily: tpl?.headingFont }} noWrap>
              {sees("contact_name") && venue.contactName ? venue.contactName : venue.name}
            </Typography>
          </Box>
        </Stack>
      ) : address ? (
        <Box>
          <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: isLuxury ? "#B45309" : isVibrant ? "#724E41" : isStudio ? "#0F172A" : "#9ca3af" }}>VISITING</Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{[venue.address, venue.area].filter(Boolean).join(", ")}</Typography>
        </Box>
      ) : null}
      {lines.length > 0 && (
        <Box sx={{ mt: 1.25, borderRadius: isLuxury ? "4px" : isVibrant ? "12px" : isStudio ? "6px" : "8px", border: isLuxury ? "1px solid #E8DFD3" : isVibrant ? "1px solid #E8D8CB" : isStudio ? "1px solid #CBD5E1" : "1px solid #eef0f3", overflow: "hidden" }}>
          {lines.map((line, index) => (
            <Stack key={line.key} direction="row" spacing={0.75} alignItems="center" sx={{ px: 1, py: 0.75, borderTop: index ? (isLuxury ? "1px solid #E8DFD3" : isVibrant ? "1px solid #E8D8CB" : isStudio ? "1px solid #CBD5E1" : "1px solid #eef0f3") : 0, fontSize: 10.5, fontWeight: 600, "& svg": { fontSize: 14, color: accent } }}>
              {line.icon}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.text}</span>
            </Stack>
          ))}
        </Box>
      )}
      {notes.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.25, p: 1, borderRadius: 1.5, bgcolor: soft }}>
          {notes.map((note) => (
            <Box key={note.key}>
              <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: 0.6, color: "#9ca3af", textTransform: "uppercase" }}>{note.label}</Typography>
              <Line width={note.key === "time_zone" ? "45%" : "85%"} />
            </Box>
          ))}
        </Stack>
      )}
      {directions && (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mt: 1, py: 0.6, borderRadius: tpl?.pillRadius ?? 1.5, border: isLuxury ? "1px solid #B45309" : isVibrant ? "1px solid #724E41" : "1px solid #e5e7eb", bgcolor: isLuxury ? alpha("#B45309", 0.08) : isVibrant ? alpha("#724E41", 0.08) : "transparent", color: isLuxury ? "#B45309" : isVibrant ? "#724E41" : "inherit", fontSize: 11, fontWeight: 700 }}>
          <DirectionsOutlined sx={{ fontSize: 14 }} />
          <span>Get directions</span>
        </Stack>
      )}
      {socials.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
          {socials.map(([key, icon]) => (
            <Box key={key as string} sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: soft, display: "grid", placeItems: "center", "& svg": { fontSize: 13, color: accent } }}>{icon}</Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ arrange */

function ArrangeList({
  order,
  label,
  sectionAudience,
  onOrderChange,
}: {
  order: string[];
  label: (key: string) => string;
  sectionAudience: (key: string) => Audience;
  onOrderChange?: (order: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const move = (from: number, to: number) => onOrderChange?.(arrayMove(order, from, to));
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) move(order.indexOf(String(active.id)), order.indexOf(String(over.id)));
  };
  const firstWide = order.findIndex((key) => WIDE.has(key));

  return (
    <Box sx={{ px: 1.5, pt: 5, pb: 3, bgcolor: "#f7f7fa", minHeight: "100%" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, px: 0.5 }}>Arrange the page</Typography>
      <Typography sx={{ fontSize: 11.5, color: "#6b7280", px: 0.5, mb: 1.5 }}>
        {onOrderChange ? "Drag sections, or use the arrows." : "Only Admins can change the order."}
      </Typography>

      <FixedRow icon={<StorefrontOutlined />} text="Name, photos and key facts" />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {order.map((key, index) => (
              <Box key={key}>
                {index === firstWide && <FixedRow icon={<CallOutlined />} text="Contact card" />}
                <SortableRow
                  id={key}
                  label={label(key)}
                  icon={SECTION_ICONS[key]}
                  audience={sectionAudience(key)}
                  disabled={!onOrderChange}
                  onUp={index > 0 ? () => move(index, index - 1) : undefined}
                  onDown={index < order.length - 1 ? () => move(index, index + 1) : undefined}
                />
              </Box>
            ))}
            {firstWide === -1 && <FixedRow icon={<CallOutlined />} text="Contact card" />}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}

function FixedRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Tooltip title="Stays in place" placement="left">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1, my: 0.75, borderRadius: 2, border: "1px dashed #d1d5db", color: "#9ca3af", "& svg": { fontSize: 17 } }}>
        {icon}
        <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>{text}</Typography>
        <LockOutlined sx={{ fontSize: "14px !important" }} />
      </Stack>
    </Tooltip>
  );
}

function SortableRow({
  id,
  label,
  icon,
  audience,
  disabled,
  onUp,
  onDown,
}: {
  id: string;
  label: string;
  icon: ReactNode;
  audience: Audience;
  disabled: boolean;
  onUp?: () => void;
  onDown?: () => void;
}) {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const hidden = audience === "hidden";
  const tone = toneColor(theme, audience);
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition }}
      sx={{
        position: "relative",
        zIndex: isDragging ? 2 : 0,
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 0.5,
        pr: 0.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: isDragging ? "primary.main" : "#e5e7eb",
        boxShadow: isDragging ? "0 12px 28px rgba(17,24,39,.18)" : "0 1px 2px rgba(16,24,40,.05)",
        opacity: hidden ? 0.6 : 1,
      }}
    >
      <ButtonBase
        {...attributes}
        {...listeners}
        disabled={disabled}
        aria-label={`Drag ${label}`}
        sx={{ p: 0.5, borderRadius: 1, color: "#9ca3af", cursor: disabled ? "default" : isDragging ? "grabbing" : "grab", touchAction: "none" }}
      >
        <DragIndicator sx={{ fontSize: 18 }} />
      </ButtonBase>
      <Box sx={{ display: "flex", color: hidden ? "#9ca3af" : "#4b5563", "& svg": { fontSize: 18 } }}>{icon}</Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{label}</Typography>
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone, flexShrink: 0 }} aria-hidden />
      {!disabled && (
        <Stack direction="row">
          <IconButton size="small" aria-label={`Move ${label} up`} disabled={!onUp} onClick={onUp} sx={{ p: 0.25 }}>
            <ArrowUpward sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton size="small" aria-label={`Move ${label} down`} disabled={!onDown} onClick={onDown} sx={{ p: 0.25 }}>
            <ArrowDownward sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
